import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useRef, useState, type RefObject } from 'react'
import type { MenuItem as MenuItemType } from '@/api/types'
import { ExtraItemPicker } from '@/pages/quotations/ExtraItemPicker'
import { formatMoney } from '@/components/common'

/**
 * One line of something sold: a dish off the catalogue, a quantity and a rate.
 *
 * <p>{@code menuItemId} is empty for a line typed in by hand, which the counter
 * still needs for the thing that is not on the menu.</p>
 */
export type BillLine = {
  key: string
  /**
   * A dish, or a whole package sold as one lot.
   *
   * <p>A package is normally priced per head times the pax. A sitting sold by
   * the quantity has no pax, so it goes on as a line like anything else.</p>
   */
  kind?: 'ITEM' | 'PACKAGE'
  packageId?: string
  menuItemId: string
  item: MenuItemType | null
  description: string
  quantity: string
  unit: string
  unitPrice: string
  /**
   * The rate this line is taxed at. Defaults to whatever the catalogue item
   * carries, but stays editable like the rate itself - a line agreed at a
   * different tax treatment is not something the counter can fix by typing
   * over the catalogue.
   */
  taxRatePercent: string
  /**
   * Whether the rate above already has that tax in it. Taken from the
   * catalogue item, not asked for here - a dish priced "all in" is not
   * something a counter decides line by line, and getting it wrong adds the
   * tax a second time on top of a rate that already carries it.
   */
  priceInclusiveTax?: boolean
}

export const blankLine = (taxRatePercent = '5'): BillLine => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  kind: 'ITEM',
  packageId: '',
  menuItemId: '',
  item: null,
  description: '',
  quantity: '1',
  unit: 'NOS',
  unitPrice: '',
  taxRatePercent,
})

/** What the line comes to. Money off is agreed on the bill, not on a line. */
const amountOf = (line: BillLine) =>
  Number(line.quantity || 0) * Number(line.unitPrice || 0)

/**
 * Entering what is sold, the way a counter enters it.
 *
 * <p>The billing screen this is taken from is one row: find the dish, say how
 * many, see the amount, add. The rate comes with the dish and stays editable,
 * because a counter is often quoted at something other than the list price.
 * Enter moves through it and adds the line, so a whole bill is typed without
 * touching the mouse - which is the only way it gets typed at a counter with
 * somebody waiting.</p>
 *
 * <p>What is added lands in a numbered table underneath. The same component
 * serves a quotation, an order and a bill, so the three cannot drift into
 * entering the same thing three ways.</p>
 */
export function BillLines({
  lines,
  setLines,
  title = 'What is sold',
  helper,
  ratesEditable = true,
}: {
  lines: BillLine[]
  setLines: (lines: BillLine[]) => void
  title?: string
  helper?: string
  /** Off, a line takes the catalogue's own rate and nobody here may change it. */
  ratesEditable?: boolean
}) {
  const [picked, setPicked] = useState<MenuItemType | null>(null)
  const [quantity, setQuantity] = useState('1')
  const [unitPrice, setUnitPrice] = useState('')
  const [taxRate, setTaxRate] = useState('5')
  const searchBox = useRef<HTMLInputElement>(null)
  const quantityBox = useRef<HTMLInputElement>(null)
  const rateBox = useRef<HTMLInputElement>(null)
  const taxBox = useRef<HTMLInputElement>(null)
  const amountBox = useRef<HTMLInputElement>(null)

  /**
   * Enter runs the length of the row and then starts the next one.
   *
   * <p>Catalogue, quantity, rate, amount, and back to the catalogue once the
   * line is down - so a bill of twenty items is twenty passes of the same four
   * keystrokes and the hand never leaves the keyboard.</p>
   */
  const moveTo = (box: RefObject<HTMLInputElement | null>) =>
    window.setTimeout(() => {
      box.current?.focus()
      box.current?.select()
    }, 0)

  const staged = Number(quantity || 0) * Number(unitPrice || 0)
  const canAdd = Number(quantity) > 0 && Boolean(picked)

  /** Choosing a dish brings its rate, its unit and its tax rate, and moves to the quantity. */
  const choose = (item: MenuItemType | null) => {
    setPicked(item)
    setUnitPrice(item ? String(item.sellingPrice ?? '') : '')
    setTaxRate(item ? String(item.taxRatePercent ?? 5) : '5')
    if (item) {
      // The next thing anybody types is how many, so put them there.
      moveTo(quantityBox)
    }
  }

  const add = () => {
    if (!canAdd) return
    setLines([
      ...lines,
      {
        ...blankLine(taxRate),
        menuItemId: picked!.id,
        item: picked,
        description: picked!.name,
        quantity,
        unit: picked!.servingUnit ?? 'NOS',
        unitPrice,
        priceInclusiveTax: picked?.priceInclusiveTax,
      },
    ])
    setPicked(null)
    setQuantity('1')
    setUnitPrice('')
    setTaxRate('5')
    // Back to the catalogue for the next line, which is the whole point.
    moveTo(searchBox)
  }

  const update = (index: number, patch: Partial<BillLine>) =>
    setLines(lines.map((line, at) => (at === index ? { ...line, ...patch } : line)))

  // A line already priced inclusive of tax discloses what it contains rather
  // than having more added on top of it - a rate that already carries GST
  // billed again at the foot would charge it twice.
  const lineTax = (line: BillLine) => {
    const amount = amountOf(line)
    const rate = Number(line.taxRatePercent || 0) / 100
    if (rate <= 0) {
      return { tax: 0, addsToTotal: 0 }
    }
    if (line.priceInclusiveTax) {
      const net = amount / (1 + rate)
      return { tax: amount - net, addsToTotal: 0 }
    }
    const added = amount * rate
    return { tax: added, addsToTotal: added }
  }

  const total = lines.reduce((sum, line) => sum + amountOf(line), 0)
  const tax = lines.reduce((sum, line) => sum + lineTax(line).tax, 0)
  const addedTax = lines.reduce((sum, line) => sum + lineTax(line).addsToTotal, 0)

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2">{title}</Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {helper}
        </Typography>
      ) : null}

      {/* Find it, say how many, add it. One row, and Enter runs the length of
          it, because a bill gets typed with somebody waiting at the counter. */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <Box sx={{ flex: '2 1 240px', minWidth: 200 }}>
            <ExtraItemPicker
              label="Search by name or code"
              value={picked}
              onChange={choose}
              inputRef={searchBox}
            />
          </Box>
          <TextField
            size="small"
            type="number"
            label="Qty"
            value={quantity}
            inputRef={quantityBox}
            onChange={(event) => setQuantity(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                moveTo(rateBox)
              }
            }}
            inputProps={{ min: 0, 'aria-label': 'Quantity to add' }}
            sx={{ width: 90 }}
          />
          <TextField
            size="small"
            type="number"
            label="Rate"
            value={unitPrice}
            inputRef={rateBox}
            disabled={!ratesEditable}
            onChange={(event) => setUnitPrice(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                moveTo(taxBox)
              }
            }}
            inputProps={{ min: 0, 'aria-label': 'Rate to add' }}
            helperText={
              !ratesEditable
                ? 'Fixed by the catalogue for this company'
                : picked
                  ? 'From the catalogue; change it if it was agreed otherwise'
                  : ' '
            }
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            type="number"
            label="GST %"
            value={taxRate}
            inputRef={taxBox}
            disabled={!ratesEditable}
            onChange={(event) => setTaxRate(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                moveTo(amountBox)
              }
            }}
            inputProps={{ min: 0, max: 100, 'aria-label': 'GST % to add' }}
            sx={{ width: 100 }}
          />
          {/* Read-only, but still a stop on the way: it is the last thing
              anybody checks before committing the line, and Enter here is
              what puts it down. */}
          <TextField
            size="small"
            label="Amount"
            value={staged > 0 ? formatMoney(staged) : ''}
            inputRef={amountBox}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                add()
              }
            }}
            InputProps={{ readOnly: true }}
            inputProps={{ 'aria-label': 'Amount to add' }}
            sx={{ width: 130 }}
          />
          <Button variant="contained" disabled={!canAdd} onClick={add} sx={{ mt: 0.25 }}>
            Add
          </Button>
        </Stack>
      </Paper>

      {lines.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          Nothing added yet — find an item above to add it.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 44 }}>#</TableCell>
              <TableCell>Item</TableCell>
              <TableCell sx={{ width: 90 }}>Qty</TableCell>
              <TableCell sx={{ width: 90 }}>Unit</TableCell>
              <TableCell sx={{ width: 110 }}>Rate</TableCell>
              <TableCell sx={{ width: 90 }}>GST %</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>
                Amount
              </TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, index) => (
              <TableRow key={line.key}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2">{line.description}</Typography>
                  {line.item ? (
                    <Typography variant="caption" color="text.secondary">
                      {line.item.itemCode}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    type="number"
                    value={line.quantity}
                    onChange={(event) => update(index, { quantity: event.target.value })}
                    inputProps={{ min: 0, 'aria-label': `Quantity ${index + 1}` }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    value={line.unit}
                    onChange={(event) => update(index, { unit: event.target.value })}
                    inputProps={{ 'aria-label': `Unit ${index + 1}` }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    type="number"
                    value={line.unitPrice}
                    disabled={!ratesEditable}
                    onChange={(event) => update(index, { unitPrice: event.target.value })}
                    inputProps={{ min: 0, 'aria-label': `Rate ${index + 1}` }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    type="number"
                    value={line.taxRatePercent}
                    disabled={!ratesEditable}
                    onChange={(event) => update(index, { taxRatePercent: event.target.value })}
                    inputProps={{ min: 0, max: 100, 'aria-label': `GST % ${index + 1}` }}
                  />
                </TableCell>
                <TableCell align="right">{formatMoney(amountOf(line), true)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Take this line off">
                    <IconButton
                      size="small"
                      aria-label={`Remove ${line.description || index + 1}`}
                      onClick={() => setLines(lines.filter((_, at) => at !== index))}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={6} align="right">
                Total
              </TableCell>
              <TableCell align="right">{formatMoney(total, true)}</TableCell>
              <TableCell />
            </TableRow>
            {/* Only where GST actually adds to Total - a line already priced
                inclusive has nothing to disclose here that Total doesn't
                already say, so a table of inclusive lines alone shows no tax
                row at all. */}
            {addedTax > 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="right">
                  GST
                </TableCell>
                <TableCell align="right">{formatMoney(tax, true)}</TableCell>
                <TableCell />
              </TableRow>
            ) : null}
            {addedTax > 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="right">
                  <strong>Total incl. GST</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{formatMoney(total + addedTax, true)}</strong>
                </TableCell>
                <TableCell />
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}
